import { useState, type JSX } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Container,
  Title,
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Divider,
  Alert,
  Center,
} from '@mantine/core';
import { IconBrandGoogle } from '@tabler/icons-react';
import { useAuth } from './auth-context';

export function LoginPage(): JSX.Element {
  const { signInWithMagicLink, signInWithPassword, signInWithGoogle, user, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  const handlePassword = async (): Promise<void> => {
    setSubmitting(true);
    setMessage(null);
    const { error } = await signInWithPassword(email, password);
    setSubmitting(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleMagicLink = async (): Promise<void> => {
    setSubmitting(true);
    setMessage(null);
    const { error } = await signInWithMagicLink(email);
    setSubmitting(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setMessage({ type: 'success', text: 'Check your email for the magic link' });
    }
  };

  const handleGoogle = async (): Promise<void> => {
    setMessage(null);
    const { error } = await signInWithGoogle();
    if (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  return (
    <Center h="100vh">
      <Container size="xs" w={400}>
        <Title order={2} ta="center" mb="xl">
          Wandero Admin
        </Title>
        <Stack>
          <TextInput
            label="Email"
            placeholder="you@wandero.pl"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            type="email"
            aria-label="Email address"
          />
          <PasswordInput
            label="Password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            aria-label="Password"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && email && password) handlePassword();
            }}
          />
          <Button
            loading={submitting}
            disabled={!email || !password}
            onClick={handlePassword}
          >
            Sign in
          </Button>
          <Divider label="or" labelPosition="center" />
          <Button
            variant="default"
            loading={submitting}
            disabled={!email}
            onClick={handleMagicLink}
          >
            Send magic link
          </Button>
          <Button
            leftSection={<IconBrandGoogle size={18} aria-hidden />}
            variant="default"
            onClick={handleGoogle}
          >
            Sign in with Google
          </Button>
          {message && (
            <Alert color={message.type === 'error' ? 'red' : 'green'} role="status">
              {message.text}
            </Alert>
          )}
        </Stack>
      </Container>
    </Center>
  );
}
